import {
  ECardinality,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  IConditionModel,
  IDisplayItem,
  IEvent,
  IIfThenElseDataModel,
  ISymbol,
  ITargetScreenInputParam
} from "@kuika/kuika-cl-model";
import {
  ConditionSetType,
  IForEachDataModel,
  IKType,
  IManagedDbDataModel,
  IMqttActionsDataModel,
  IMultiReportBlock,
  IMultiReportInstance,
  IMultiReportPrintData,
  IRecurringActionsDataModel
} from "@kuika/kuika-cl-model/src/models/builder.dtos";
import { CascaderOptionType, CascaderValueType } from "antd/es/cascader";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { IWithSDProps } from "../hoc/screen-design-consumer";
import { type ScreenDesignInstance } from "./designer-context";
import { buildDesignerSymbol } from "./designer-property-actions";
import {
  DesignerSymbolPickerEngine,
  getDesignerSymbolPickerSelectedOptionsByPath,
  ISymbolPickerCascaderProps,
  serializeDesignerSymbolPickerOptions
} from "./designer-symbol-picker-engine";
import { safeParseJsonish, parseOptionPath } from "./designer-utils";
import { resolveEditTarget, type IScreenScopedInput } from "./detached-screen-session";

export function ensureDesignerActionParameters(action: IAction): IAction {
  const nextAction = _.cloneDeep(action);
  nextAction.parameters.forEach((parameter) => {
    if (parameter.symbol === undefined) {
      parameter.symbol = getDefaultDesignerActionSymbol(parameter.Typename);
    }
  });
  return nextAction;
}

/** A boolean / switch action parameter (SQL `bit` column → Typename
 *  `"Boolean"`, or `bool`/`bit` casings). Such a parameter has no "unset"
 *  state — its default must be `false`, never an empty string. An empty
 *  string fails the backend's `Symbol.IsEmpty()` check (KV1012) at codegen. */
export function isBooleanActionParameterType(typeName?: string): boolean {
  const t = typeName?.toLowerCase?.() ?? "";
  return t === "boolean" || t === "bool" || t === "bit";
}

export function getDefaultDesignerActionSymbol(typeName?: string): ISymbol {
  return {
    category: ESymbolCategory.fixed,
    fixedValue: isBooleanActionParameterType(typeName) ? false : "",
    currentType: typeName,
    targetType: typeName,
    targetCardinality: ECardinality.One,
    description: "",
    label: "Fixed Value"
  } as ISymbol;
}

export function setDesignerActionParameter(
  action: IAction,
  actionParameter: IActionParameter,
  symbol?: ISymbol | null
): IAction {
  const nextAction: IAction = _.cloneDeep(action);
  const nextSymbol = symbol ? _.cloneDeep(symbol) : getDefaultDesignerActionSymbol(actionParameter.Typename);

  let parameter = findDesignerActionParameter(
    nextAction.parameters,
    actionParameter.name,
    actionParameter.ownerPropName
  );

  // The top-level shortcut below only makes sense for a signature (top-level) param. A nested field
  // (ownerPropName set) must not be routed to a same-named top-level param — that was the collision
  // that let a nested Company.Name edit overwrite the object's own top-level Name.
  if (actionParameter && actionParameter.ownerPropName === undefined) {
    const existingParameter = nextAction.parameters.find((candidate) => candidate.name === actionParameter.name);
    if (existingParameter) {
      existingParameter.symbol = _.cloneDeep(nextSymbol);
    }
  }

  if (parameter) {
    parameter.symbol = nextSymbol;
    if (parameter.isObject) {
      if (
        parameter.Typename === nextSymbol.targetType?.split(".").pop() &&
        !(nextSymbol.category === ESymbolCategory.default && nextSymbol.defaultValueName === "Custom Binding")
      ) {
        parameter = updateNestedDesignerActionParameters(parameter, nextSymbol);
      } else {
        updateNestedDesignerActionParameters(parameter, nextSymbol);
        parameter.isObjectCollapsed = false;
      }
    }
    parameter.Typename = actionParameter.Typename;
    if (actionParameter.isDeletedOnSource === true) {
      parameter.isDeletedOnSource = true;
    }
  } else {
    // A NAVIGATE action's screen-input params live on `targetScreenInputParams`,
    // not the signature `parameters`. Bind the symbol there (e.g. current.Id on the
    // row-id screen input) instead of pushing a phantom signature parameter.
    const screenInputParam = Array.isArray(nextAction.targetScreenInputParams)
      ? nextAction.targetScreenInputParams.find((candidate) => candidate.name === actionParameter.name)
      : undefined;
    if (screenInputParam) {
      screenInputParam.symbol = nextSymbol;
    } else {
      const parameterClone = _.cloneDeep(actionParameter);
      parameterClone.symbol = nextSymbol;
      nextAction.parameters.push(parameterClone);
    }
  }

  return nextAction;
}

export function setMultipleDesignerActionParameters(
  action: IAction,
  actionParameters: IActionParameter[],
  changeReason?: ESdChangeReason
): { action: IAction; changeReason?: ESdChangeReason } {
  let nextAction = _.cloneDeep(action);
  nextAction.parameters = [];

  actionParameters.forEach((parameter) => {
    nextAction = setDesignerActionParameter(nextAction, parameter, parameter.symbol);
  });

  return { action: nextAction, changeReason };
}

export function updateNestedDesignerActionParameters(parameter: IActionParameter, symbol: ISymbol): IActionParameter {
  parameter.parameters?.forEach((childParameter) => {
    const emptySymbol = { category: ESymbolCategory.fixed, fixedValue: "" } as ISymbol;
    const derivedSymbol = _.cloneDeep(symbol);

    // Derive the child's display description in parallel with its fieldName so a whole-object binding
    // shows each field's real source path (e.g. testbo2.Alias, testbo2.Company.Name) instead of the
    // parent's binding repeated. Applies to any navigable source (action result, process parameter,
    // screen input, …), not just action results — description is a UI label; codegen reads fieldName.
    if (symbol.description) {
      derivedSymbol.description = `${symbol.description}.${childParameter.realPropName}`;
    }

    derivedSymbol.currentType = childParameter.Typename;
    derivedSymbol.targetType = childParameter.Typename;
    derivedSymbol.targetCardinality = childParameter.cardinality;
    derivedSymbol.fieldName = symbol.fieldName
      ? `${symbol.fieldName}.${childParameter.realPropName}`
      : childParameter.realPropName;

    childParameter.symbol = parameter.Typename === symbol.targetType?.split(".").pop() ? derivedSymbol : emptySymbol;

    if (childParameter.isObject && childParameter.parameters && childParameter.parameters.length > 0) {
      updateNestedDesignerActionParameters(childParameter, childParameter.symbol);
    }
  });

  return parameter;
}

export function setDesignerActionValues(
  action: IAction,
  values: {
    actionParameters?: IActionParameter[];
    targetScreenInputParams?: ITargetScreenInputParam[];
    ifThenElseData?: IIfThenElseDataModel;
    actionReturnTypes?: IKType;
  },
  changeReason?: ESdChangeReason
): { action: IAction; changeReason?: ESdChangeReason } {
  let nextAction = _.cloneDeep(action);

  if (values.actionParameters) {
    values.actionParameters.forEach((parameter) => {
      const nextParameter = _.cloneDeep(parameter);
      if (!nextParameter.symbol) {
        nextParameter.symbol = getDefaultDesignerActionSymbol(nextParameter.Typename);
      }
      nextAction = setDesignerActionParameter(nextAction, nextParameter, nextParameter.symbol);
    });
  }

  if (values.ifThenElseData) {
    nextAction.ifThenElseData = _.cloneDeep(values.ifThenElseData);
  }

  if (values.targetScreenInputParams) {
    nextAction.targetScreenInputParams = _.cloneDeep(values.targetScreenInputParams);
  }

  if (values.actionReturnTypes) {
    nextAction.returnTypeDynamicInterface = _.cloneDeep(values.actionReturnTypes);
  }

  return { action: nextAction, changeReason };
}

export function setDesignerActionIfThenElseCondition(
  action: IAction,
  condition: IConditionModel,
  changeReason?: ESdChangeReason
): { action: IAction; changeReason?: ESdChangeReason } {
  const nextAction = _.cloneDeep(action);
  if (nextAction.ifThenElseData === undefined) {
    nextAction.ifThenElseData = {
      ifActions: [],
      elseActions: [],
      condition
    } as IIfThenElseDataModel;
  } else {
    nextAction.ifThenElseData.condition = condition;
  }
  return { action: nextAction, changeReason };
}

export function setDesignerActionCondition(
  action: IAction,
  condition: IConditionModel | undefined,
  changeReason?: ESdChangeReason
): { action: IAction; changeReason?: ESdChangeReason } {
  const nextAction = _.cloneDeep(action);
  nextAction.condition = _.cloneDeep(condition);
  return { action: nextAction, changeReason };
}

export function addDesignerActionBlankCondition(action: IAction): IAction {
  return setDesignerActionCondition(action, {
    conditionSet: {
      conditions: [],
      type: ConditionSetType.and
    }
  } as IConditionModel).action;
}

export function removeDesignerActionCondition(action: IAction): IAction {
  return setDesignerActionCondition(action, undefined).action;
}

export function setDesignerActionForEachData(
  action: IAction,
  forEachData: IForEachDataModel,
  changeReason?: ESdChangeReason
): { action: IAction; changeReason?: ESdChangeReason } {
  const nextAction = _.cloneDeep(action);
  nextAction.forEachData = _.cloneDeep(forEachData);
  return { action: nextAction, changeReason };
}

export function setDesignerActionRecurringActionsData(
  action: IAction,
  recurringActionsData: IRecurringActionsDataModel,
  changeReason?: ESdChangeReason
): { action: IAction; changeReason?: ESdChangeReason } {
  const nextAction = _.cloneDeep(action);
  nextAction.recurringActionsData = _.cloneDeep(recurringActionsData);
  return { action: nextAction, changeReason };
}

export function setDesignerActionMultiReportData(
  action: IAction,
  multiReportData: IMultiReportPrintData,
  changeReason?: ESdChangeReason
): { action: IAction; changeReason?: ESdChangeReason } {
  const nextAction = _.cloneDeep(action);
  nextAction.multiReportData = _.cloneDeep(multiReportData);
  return { action: nextAction, changeReason };
}

export function setDesignerActionManagedDbData(
  action: IAction,
  managedDbData: IManagedDbDataModel,
  changeReason?: ESdChangeReason
): { action: IAction; changeReason?: ESdChangeReason } {
  const nextAction = _.cloneDeep(action);
  nextAction.managedDbData = _.cloneDeep(managedDbData);
  return { action: nextAction, changeReason };
}

export function setDesignerActionMqttSubscribeData(
  action: IAction,
  mqttSubscribeActionData: IMqttActionsDataModel,
  changeReason?: ESdChangeReason
): { action: IAction; changeReason?: ESdChangeReason } {
  const nextAction = _.cloneDeep(action);
  nextAction.mqttSubscribeActionData = _.cloneDeep(mqttSubscribeActionData);
  return { action: nextAction, changeReason };
}

export type DesignerActionCollectionType = "if" | "else" | "forEach" | "recurring" | "mqttSubscribe";

export function getDesignerActionCollectionActions(
  action: IAction,
  collectionType: DesignerActionCollectionType
): IAction[] {
  switch (collectionType) {
    case "if":
      return action.ifThenElseData?.ifActions || [];
    case "else":
      return action.ifThenElseData?.elseActions || [];
    case "forEach":
      return action.forEachData?.actions || [];
    case "recurring":
      return action.recurringActionsData?.recurringActions || [];
    case "mqttSubscribe":
      return action.mqttSubscribeActionData?.mqttSubscribeActions || [];
    default:
      return [];
  }
}

export function setDesignerActionCollectionActions(
  action: IAction,
  collectionType: DesignerActionCollectionType,
  actions: IAction[],
  changeReason?: ESdChangeReason
): { action: IAction; changeReason?: ESdChangeReason } {
  const nextAction = _.cloneDeep(action);

  switch (collectionType) {
    case "if":
      if (!nextAction.ifThenElseData) {
        nextAction.ifThenElseData = {
          condition: {} as IConditionModel,
          ifActions: [],
          elseActions: []
        } as IIfThenElseDataModel;
      }
      nextAction.ifThenElseData.ifActions = _.cloneDeep(actions);
      break;
    case "else":
      if (!nextAction.ifThenElseData) {
        nextAction.ifThenElseData = {
          condition: {} as IConditionModel,
          ifActions: [],
          elseActions: []
        } as IIfThenElseDataModel;
      }
      nextAction.ifThenElseData.elseActions = _.cloneDeep(actions);
      break;
    case "forEach":
      if (!nextAction.forEachData) {
        nextAction.forEachData = { actions: [] } as unknown as IForEachDataModel;
      }
      nextAction.forEachData.actions = _.cloneDeep(actions);
      break;
    case "recurring":
      if (!nextAction.recurringActionsData) {
        nextAction.recurringActionsData = { recurringActions: [] } as IRecurringActionsDataModel;
      }
      nextAction.recurringActionsData.recurringActions = _.cloneDeep(actions);
      break;
    case "mqttSubscribe":
      if (!nextAction.mqttSubscribeActionData) {
        nextAction.mqttSubscribeActionData = { mqttSubscribeActions: [] } as IMqttActionsDataModel;
      }
      nextAction.mqttSubscribeActionData.mqttSubscribeActions = _.cloneDeep(actions);
      break;
    default:
  }

  return { action: nextAction, changeReason };
}

export function addDesignerActionToCollection(
  action: IAction,
  collectionType: DesignerActionCollectionType,
  actionToAdd: IAction,
  changeReason?: ESdChangeReason
): { action: IAction; changeReason?: ESdChangeReason } {
  const currentActions = getDesignerActionCollectionActions(action, collectionType);
  return setDesignerActionCollectionActions(
    action,
    collectionType,
    [...currentActions, _.cloneDeep(actionToAdd)],
    changeReason
  );
}

export function reorderDesignerActionCollection(
  action: IAction,
  collectionType: DesignerActionCollectionType,
  sourceIndex: number,
  destinationIndex: number,
  changeReason?: ESdChangeReason
): { action: IAction; changeReason?: ESdChangeReason } {
  const currentActions = [...getDesignerActionCollectionActions(action, collectionType)];
  const [moved] = currentActions.splice(sourceIndex, 1);
  if (!moved) {
    return { action: _.cloneDeep(action), changeReason };
  }

  currentActions.splice(destinationIndex, 0, moved);
  return setDesignerActionCollectionActions(action, collectionType, currentActions, changeReason);
}

export function setDesignerActionConfirmation(
  action: IAction,
  confirmationMessage: string | undefined,
  fontFamily: string | undefined,
  confirmationHeader: string | undefined,
  confirmationIcon: string | undefined,
  confirmationIconBackgroundColor: string | undefined,
  confirmationConfirmButtonColor: string | undefined,
  confirmationCancelButtonColor: string | undefined,
  changeReason: ESdChangeReason = ESdChangeReason.ConfirmationMessageChanged
): { action: IAction; changeReason?: ESdChangeReason } {
  const nextAction = _.cloneDeep(action);
  nextAction.confirmationMessage = confirmationMessage;
  nextAction.confirmationFontFamily = fontFamily;
  nextAction.confirmationHeader = confirmationHeader;
  nextAction.confirmationIcon = confirmationIcon;
  nextAction.confirmationIconBackgroundColor = confirmationIconBackgroundColor;
  nextAction.confirmationConfirmButtonColor = confirmationConfirmButtonColor;
  nextAction.confirmationCancelButtonColor = confirmationCancelButtonColor;
  return { action: nextAction, changeReason };
}

export interface AddConfirmationOptions {
  message?: string;
  fontFamily?: string;
  header?: string;
  icon?: string;
  iconBackgroundColor?: string;
  confirmButtonColor?: string;
  cancelButtonColor?: string;
}

/**
 * Add a confirmation dialog to an action. Each field falls back to the legacy
 * hardcoded default when not supplied, so existing callers (the manual
 * properties-panel "add confirmation") keep their current behavior; the AI path
 * passes localized, context-aware overrides via `opts`. Button colors have no
 * default: left unset they fall back to the generated app's theme colors.
 */
export function addDesignerActionConfirmation(action: IAction, opts?: AddConfirmationOptions): IAction {
  return setDesignerActionConfirmation(
    action,
    opts?.message ?? "Confirm",
    opts?.fontFamily ?? "Poppins",
    opts?.header ?? "Confirmation",
    opts?.icon ?? "confirmation",
    opts?.iconBackgroundColor ?? "#246dff",
    opts?.confirmButtonColor,
    opts?.cancelButtonColor
  ).action;
}

export function removeDesignerActionConfirmation(action: IAction): IAction {
  return setDesignerActionConfirmation(
    action,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined
  ).action;
}

export function setDesignerActionRecurringInterval(
  action: IAction,
  recurringSec: number | undefined | null,
  changeReason: ESdChangeReason = ESdChangeReason.RecurringIntervalChange
): { action: IAction; changeReason?: ESdChangeReason } {
  const nextAction = _.cloneDeep(action);
  if (recurringSec === null) {
    nextAction.isRecurring = false;
    delete nextAction.recurringInterval;
  } else {
    nextAction.recurringInterval = recurringSec;
    nextAction.isRecurring = true;
  }
  return { action: nextAction, changeReason };
}

export function addDesignerActionRecurring(action: IAction): IAction {
  return setDesignerActionRecurringInterval(action, 0).action;
}

export function removeDesignerActionRecurring(action: IAction): IAction {
  return setDesignerActionRecurringInterval(action, null).action;
}

export function setDesignerActionDontTriggerValidations(action: IAction, dontTriggerValidations: boolean): IAction {
  const nextAction = _.cloneDeep(action);
  nextAction.dontTriggerValidations = dontTriggerValidations;
  return nextAction;
}

export function setDesignerActionAddGoBackAction(action: IAction, addGoBackAction: boolean): IAction {
  const nextAction = _.cloneDeep(action);
  nextAction.addGoBackAction = addGoBackAction;
  return nextAction;
}

export function setDesignerActionDescriptionWithParameter(
  action: IAction,
  description: string,
  parameter: IActionParameter,
  symbol: ISymbol
): IAction {
  let nextAction = _.cloneDeep(action);
  nextAction.description = description;
  nextAction = setDesignerActionParameter(nextAction, parameter, symbol);
  return nextAction;
}

export function setDesignerActionTag(action: IAction, value?: string | null): IAction {
  const nextAction = _.cloneDeep(action);
  nextAction.tag = value;
  return nextAction;
}

export function setDesignerActionNote(action: IAction, value?: string | null): IAction {
  const nextAction = _.cloneDeep(action);
  nextAction.note = value;
  return nextAction;
}

export function setDesignerActionSqlLikeSetting(action: IAction, value: string | undefined): IAction {
  const nextAction = _.cloneDeep(action);
  nextAction.sqlLikeSetting = value;
  return nextAction;
}

export function setDesignerActionAuditEnabled(action: IAction, isAuditEnabled: boolean): IAction {
  const nextAction = _.cloneDeep(action);
  nextAction.isAuditEnabled = isAuditEnabled;
  nextAction.auditLevel = isAuditEnabled ? (nextAction.auditLevel ?? "Metadata") : undefined;
  return nextAction;
}

export function setDesignerActionEnabled(action: IAction, enabled: boolean): IAction {
  const nextAction: IAction = _.cloneDeep(action);
  nextAction.enabled = enabled ? undefined : false;
  return nextAction;
}

export function isDesignerActionEnabled(action: IAction): boolean {
  return action.enabled !== false;
}

export function setDesignerActionAuditLevel(
  action: IAction,
  auditLevel: string,
  changeReason: ESdChangeReason = ESdChangeReason.DiActionUpdated
): { action: IAction; changeReason?: ESdChangeReason } {
  const nextAction = _.cloneDeep(action);
  nextAction.auditLevel = auditLevel;
  return { action: nextAction, changeReason };
}

interface IBindingOptionsResult {
  targetType: string;
  targetCardinality: ECardinality;
  pickerOptions: ReturnType<typeof serializeDesignerSymbolPickerOptions>;
  supportsOptionPath: boolean;
}

export function listDesignerActionParameterBindingOptions(
  sdInstance: ScreenDesignInstance,
  action: IAction,
  parameter: IActionParameter,
  screenId?: string | null
): IBindingOptionsResult {
  const engineProps: ISymbolPickerCascaderProps & IWithSDProps = {
    // The picker offers what the SCREEN can bind (its inputs, its components, its action
    // results), so a parameter on a detached screen must be offered that screen's bindables.
    sd: resolveEditTarget(sdInstance, screenId),
    categoryName: parameter.displayName || parameter.name,
    action: _.cloneDeep(action),
    parameter: _.cloneDeep(parameter),
    symbol: _.cloneDeep(parameter.symbol),
    targetTypeName: parameter.Typename,
    targetCardinality: parameter.cardinality,
    isObject: parameter.isObject ?? false
  };
  const engine = new DesignerSymbolPickerEngine(engineProps);

  const options = engine.generateItems(parameter.Typename, parameter.cardinality);

  return {
    targetType: parameter.Typename,
    targetCardinality: parameter.cardinality,
    pickerOptions: serializeDesignerSymbolPickerOptions(options),
    supportsOptionPath: true
  };
}

interface IBuildSymbolInput extends IScreenScopedInput {
  optionPath?: unknown;
  pickerPath?: unknown;
  selectedPath?: unknown;
  bindingType?: string;
  symbol?: unknown;
  value?: unknown;
  /** See `IBuildDesignerSymbolInput.targetType` — defaulted from the parameter. */
  targetType?: string;
}

export function buildDesignerActionParameterSymbol(
  sdInstance: ScreenDesignInstance,
  action: IAction,
  parameter: IActionParameter,
  input: IBuildSymbolInput
): ISymbol | { error: string } {
  const engineProps: ISymbolPickerCascaderProps & IWithSDProps = {
    sd: resolveEditTarget(sdInstance, input.screenId),
    categoryName: parameter.displayName || parameter.name,
    action: _.cloneDeep(action),
    parameter: _.cloneDeep(parameter),
    symbol: _.cloneDeep(parameter.symbol),
    targetTypeName: parameter.Typename,
    targetCardinality: parameter.cardinality,
    isObject: parameter.isObject ?? false
  };
  const engine = new DesignerSymbolPickerEngine(engineProps);

  const options: CascaderOptionType[] = engine.generateItems(parameter.Typename, parameter.cardinality);
  const optionPath = parseOptionPath(input.optionPath ?? input.pickerPath ?? input.selectedPath);

  if (optionPath) {
    const selectedOptions = getDesignerSymbolPickerSelectedOptionsByPath(options, optionPath);
    if (!selectedOptions) {
      return {
        error: `Binding option path [${optionPath.join(", ")}] is not valid for action parameter "${parameter.name}"`
      };
    }

    const symbol = engine.buildSymbolFromSelectedOptions(optionPath as CascaderValueType, selectedOptions);
    if (!symbol) {
      return { error: `Could not build symbol for action parameter "${parameter.name}"` };
    }

    return symbol;
  }

  if (input.bindingType) {
    // KOA-18136 — hand the PARAMETER's declared type down as the bound-to side,
    // so a binding whose source has a fixed runtime type (a process-bag value is
    // always a String) records the conversion the picker would have recorded.
    // Never overrides a targetType the caller stated itself.
    const withTargetType = {
      ...input,
      ...(input.targetType === undefined && parameter.Typename !== undefined
        ? { targetType: String(parameter.Typename) }
        : {})
    };
    return buildDesignerSymbol(withTargetType as Parameters<typeof buildDesignerSymbol>[0], sdInstance);
  }

  if (input.symbol) {
    return safeParseJsonish(input.symbol) as ISymbol;
  }

  if (input.value !== undefined) {
    return {
      category: ESymbolCategory.fixed,
      isFixed: true,
      label: "Fixed Value",
      fixedValue: input.value,
      description: ""
    } as ISymbol;
  }

  return { error: `optionPath, bindingType, symbol, or value is required for action parameter "${parameter.name}"` };
}

export function findDesignerActionParameter(
  parameters: IActionParameter[] | undefined,
  parameterName: string,
  ownerName?: string
): IActionParameter | undefined {
  // KOA-17394 — case-insensitive parameter name lookup. BRAID emits lowercase
  // identifiers in inline trigger params (`with id=current.Id`) per its grammar,
  // but action signatures from the backend SQL layer come through with the
  // original column casing (`Id`). A strict `===` here silently dropped every
  // BRAID-inline parameter whose case didn't match the signature, leaving the
  // attached action's parameter empty and the corresponding button effectively
  // dead at runtime (Sil/Düzenle row-actions, Navigate.id).
  //
  // Nested object params keep bare field names (codegen uses `name` as the emitted
  // property key), so the same field name can legitimately appear at multiple nesting
  // levels (e.g. Company.Name and the object's own Name). When the caller knows the
  // parent field's name (ownerName), match name + owner first so a nested-field edit is
  // written to the right branch instead of the first same-named param found (the
  // collision behind "editing Company.Name updated the object's Name"). Falls back to
  // the historical name-only search when no owner is supplied or no owner-qualified match
  // exists, preserving BRAID/top-level behavior.
  const ownerQualified: IActionParameter | undefined = ownerName
    ? findParameterByNameAndOwner(parameters, parameterName.toLowerCase(), ownerName.toLowerCase())
    : undefined;
  if (ownerQualified) {
    return ownerQualified;
  }

  const target = parameterName.toLowerCase();
  for (const parameter of parameters || []) {
    if (parameter.name.toLowerCase() === target) {
      return parameter;
    }

    const nested = findDesignerActionParameter(parameter.parameters, parameterName);
    if (nested) {
      return nested;
    }
  }

  return undefined;
}

function findParameterByNameAndOwner(
  parameters: IActionParameter[] | undefined,
  targetName: string,
  targetOwner: string
): IActionParameter | undefined {
  for (const parameter of parameters || []) {
    if (parameter.name.toLowerCase() === targetName && (parameter.ownerPropName ?? "").toLowerCase() === targetOwner) {
      return parameter;
    }

    const nested = findParameterByNameAndOwner(parameter.parameters, targetName, targetOwner);
    if (nested) {
      return nested;
    }
  }

  return undefined;
}

/** One action's position inside the event that owns it: the exact list holding it, and its index there. */
export interface IDesignerActionLocation {
  event: IEvent;
  /** The live array that directly contains the action — `event.actions`, or a container action's nested list. */
  actions: IAction[];
  index: number;
}

/** Every list an action can be nested inside — kept in sync with {@link getDesignerActionCollectionActions}. */
const DESIGNER_ACTION_COLLECTION_TYPES: DesignerActionCollectionType[] = [
  "if",
  "else",
  "forEach",
  "recurring",
  "mqttSubscribe"
];

function findDesignerActionLocationInList(
  event: IEvent,
  actions: IAction[],
  actionId: string
): IDesignerActionLocation | undefined {
  const index: number = actions.findIndex((a: IAction) => a.id === actionId);
  if (index >= 0) {
    return { event, actions, index };
  }
  for (const action of actions) {
    for (const collectionType of DESIGNER_ACTION_COLLECTION_TYPES) {
      const nested: IAction[] = getDesignerActionCollectionActions(action, collectionType);
      // An absent container yields a throwaway []; skipping it keeps callers from splicing into nothing.
      if (nested.length === 0) continue;
      const hit: IDesignerActionLocation | undefined = findDesignerActionLocationInList(event, nested, actionId);
      if (hit) return hit;
    }
  }
  return undefined;
}

/**
 * Locates an action within a display item's events, returning the list that directly contains it so callers
 * can splice siblings around it. Descends into container actions (If/Then/Else branches, For Each, Recurring,
 * MQTT Subscribe), so a nested action resolves to its own branch rather than to the event's top-level list.
 */
export function findDesignerActionLocation(
  events: IEvent[] | undefined,
  actionId: string
): IDesignerActionLocation | undefined {
  for (const event of events ?? []) {
    const hit: IDesignerActionLocation | undefined = findDesignerActionLocationInList(
      event,
      event.actions ?? [],
      actionId
    );
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Adds `actionDef` as a real sibling action immediately before the GetMultipleReportsBase64 action and binds the
 * dynamic report block to its result, mutating `di` in place. Because the producing action then runs earlier in
 * the same action list, its `result.X` is available to the report action's foreach — codegen resolves it exactly
 * like a for-each collection, and the added action stays visible/editable in the normal actions list.
 * Returns false when nothing was applied, so the caller can skip the dispatch.
 */
export function addMultiReportCollectionSource(
  di: IDisplayItem | undefined,
  reportActionId: string,
  blockId: string,
  actionDef: IActionDefinitionDto
): boolean {
  const location: IDesignerActionLocation | undefined = findDesignerActionLocation(di?.item?.events, reportActionId);
  if (!location) return false;

  // Reuse an invocation of this action that already runs BEFORE the report action instead of adding a
  // duplicate; only insert a fresh one when none is present earlier in this list.
  const existing: IAction | undefined = location.actions
    .slice(0, location.index)
    .find((a: IAction) => a.actionDefinitionId === actionDef.id);
  let resultActionId: string;
  if (existing) {
    resultActionId = existing.id;
  } else {
    const collectionAction: IAction = {
      id: Guid.create().toString(),
      actionDefinitionId: actionDef.id,
      label: actionDef.name,
      description: actionDef.name,
      parameters: (actionDef.actionParameters ?? []).map((p: IActionParameter) => _.cloneDeep(p))
    } as IAction;
    // Insert as a prior sibling so its result list exists before the report action iterates it.
    location.actions.splice(location.index, 0, collectionAction);
    resultActionId = collectionAction.id;
  }

  const reportAction: IAction | undefined = location.actions.find((a: IAction) => a.id === reportActionId);
  const block: IMultiReportBlock | undefined = reportAction?.multiReportData?.reports.find(
    (b: IMultiReportBlock) => b.id === blockId
  );
  if (!block) return false;
  const elementTypeName: string | undefined = actionDef.returnTypeModel?.name;
  block.dynamicSource = {
    category: ESymbolCategory.actionResult,
    label: actionDef.name,
    description: `[${actionDef.name}]`,
    actionDefinitionId: actionDef.id,
    resultActionId: resultActionId,
    currentType: elementTypeName,
    targetType: elementTypeName,
    cardinality: ECardinality.Many
  } as ISymbol;
  // A fresh collection has a fresh element type; reset field bindings so none can mismatch.
  block.instances = [createEmptyMultiReportInstance()];
  return true;
}

/**
 * Detaches the collection: removes the added query action from the list it was added to and clears the block's
 * binding, mutating `di` in place. Symmetric with {@link addMultiReportCollectionSource}.
 * Returns false when nothing was applied.
 */
export function removeMultiReportCollectionSource(
  di: IDisplayItem | undefined,
  reportActionId: string,
  blockId: string
): boolean {
  const location: IDesignerActionLocation | undefined = findDesignerActionLocation(di?.item?.events, reportActionId);
  if (!location) return false;

  const reportAction: IAction | undefined = location.actions[location.index];
  const block: IMultiReportBlock | undefined = reportAction?.multiReportData?.reports.find(
    (b: IMultiReportBlock) => b.id === blockId
  );
  if (!block) return false;

  const resultActionId: string | undefined = block.dynamicSource?.resultActionId;
  if (resultActionId) {
    const producerIndex: number = location.actions.findIndex((a: IAction) => a.id === resultActionId);
    if (producerIndex >= 0) {
      location.actions.splice(producerIndex, 1);
    }
  }
  block.dynamicSource = undefined;
  block.instances = [createEmptyMultiReportInstance()];
  return true;
}

/** A blank output slot for a report block (its own id so React rows remount when the binding changes). */
export function createEmptyMultiReportInstance(): IMultiReportInstance {
  return { id: Guid.create().toString(), targetScreenInputParams: [] };
}
